import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon
} from '@heroicons/react/24/outline';
import { contentAPI } from '../../api/contentApi';
import { usersAPI } from '../../api/api';
import { useApiRequest } from '../../hooks/useApiRequest';
import { format } from 'date-fns';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';

const BlogList = () => {
  const navigate = useNavigate();
  const { loading, error, makeRequest } = useApiRequest();
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: ''
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  const loadPosts = async () => {
    try {
      const response = await makeRequest(() => contentAPI.getBlogPosts(filters));
      // Veriyi normalize et
      let list = [];
      if (response && Array.isArray(response.results)) list = response.results;
      else if (Array.isArray(response)) list = response;
      else if (response && Array.isArray(response.data)) list = response.data;
      else if (response && Array.isArray(response.data?.results)) list = response.data.results;

      // Yazar isimlerini zenginleştir (author_id -> full name)
      if (Array.isArray(list) && list.length > 0) {
        const uniqueAuthorIds = [...new Set(list.map(p => p.author_id).filter(Boolean))];
        const idToName = {};
        for (const uid of uniqueAuthorIds) {
          try {
            const u = await usersAPI.getUserById(uid);
            idToName[uid] = (u && (u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username)) || null;
          } catch (e) {
            idToName[uid] = null;
          }
        }
        list = list.map(p => ({
          ...p,
          author_full_name: p.author_full_name || p.author_name || idToName[p.author_id] || p.author_username
        }));
      }

      setPosts(list || []);
    } catch (err) {
      // Silently handle error
    }
  };

  useEffect(() => {
    loadPosts();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      category: '',
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Bu blog yazısını silmek istediğinizden emin misiniz?")) {
      try {
        await makeRequest(() => contentAPI.deleteBlogPost(id));
        loadPosts();
      } catch (err) {
        // Silently handle error
        alert("Blog yazısı silinirken hata oluştu.");
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'draft': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Blog Yazıları</h2>
      </div>

      <ActionToolbar className="mb-4">
        <IconActionButton
          icon={PlusIcon}
          onClick={() => navigate('/content-management/new')}
          title="Yeni Yazı"
        />
      </ActionToolbar>

      <FilterPanel
        title="Filters"
        isOpen={isFiltersOpen}
        onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
        className="mb-6"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              name="search"
              placeholder="Başlık, içerik ara..."
              value={filters.search}
              onChange={handleFilterChange}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            />
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            >
              <option value="">Tüm Durumlar</option>
              <option value="draft">Taslak</option>
              <option value="published">Yayınlandı</option>
              <option value="archived">Arşivlendi</option>
            </select>
            <input
              type="text"
              name="category"
              placeholder="Kategoriye göre filtrele"
              value={filters.category}
              onChange={handleFilterChange}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
            <FilterActions onSearch={loadPosts} onClear={handleClearFilters} />
          </div>
        </div>
      </FilterPanel>

      {loading && <p className="text-blue-500 dark:text-blue-300">Yükleniyor...</p>}
      {error && <p className="text-red-500 dark:text-red-300">Hata: {error}</p>}

      {!loading && posts.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center">Henüz blog yazısı bulunamadı.</p>
      )}

      {!loading && posts.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Başlık
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Yazar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Eylemler</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{post.title}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{post.excerpt?.substring(0, 50)}...</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {
                      post.author_display
                      || post.author_full_name
                      || (post.author_first_name || post.author_last_name ? `${post.author_first_name || ''} ${post.author_last_name || ''}`.trim() : '')
                      || post.author_name
                      || post.author_detail?.full_name
                      || post.author_detail?.username
                      || post.author_username
                      || (post.author_id ? `#${post.author_id}` : 'Bilinmiyor')
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(post.status)}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {post.created_at ? format(new Date(post.created_at), 'dd/MM/yyyy') : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <IconActionButton
                        icon={PencilIcon}
                        onClick={() => navigate(`/content-management/edit/${post.id}`)}
                        title="Düzenle"
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-200"
                      />
                      <IconActionButton
                        icon={TrashIcon}
                        onClick={() => handleDelete(post.id)}
                        title="Sil"
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogList;